// Owns ONE persistent native <video> element, created imperatively (so React
// never re-creates it) and hoisted above the AI-app routes. The "Start Your
// Session" tap on ExperienceBuilder unlocks THIS element (synchronous unmuted
// play); it survives the SPA navigation to the Session page, which then mounts
// it into its player layout and drives playback by swapping `.src` per segment.
// The unlock is per-element, so it carries across the navigation and across
// every src-swap (which the Vimeo iframe could not do).
//
// Two behaviors this relies on are verified working on iOS Safari/Chrome +
// desktop Chrome: (1) re-parenting the element (home -> Session container)
// preserves the audio unlock, and (2) the unmuted gesture-play works while the
// element sits in the off-screen "home" on ExperienceBuilder.

import transitionVideo from 'assets/videos/transition-logo_fade.mp4';

import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getAudioClientService } from 'services';
import { fetchVimeoLinks, pickSource } from 'services/vimeoVideoData';

import type { VideoT } from 'utils/video';

type SegmentSourceT = {
  mintedAt: number;
  src: null | string;
};

// Lifecycle of prepare(): 'blocked' means segment 0 (the unlock target) has no
// resolvable streaming source
type PrepareStatusT = 'blocked' | 'idle' | 'preparing' | 'ready';

// The branded transition overlay holds for at least MIN ms (so a fast load
// still shows a full intro), and at most MAX ms - a safety cap so a stalled or
// failed load can never strand it on screen.
const TRANSITION_MIN_MS = 3000;
const TRANSITION_MAX_MS = 30000;

type PlaybackContextT = {
  /**
   * Fade the branded transition overlay back out (after the minimum hold).
   * `onFadeOut`, if given, fires once the fade-out begins - use it to start a
   * clip that was held for the transition, so it's revealed from its start.
   */
  endTransition: (onFadeOut?: () => void) => void;

  /** The shared, never-recreated <video> (null during SSR / before init). */
  getVideo: () => HTMLVideoElement | null;

  /** Move the shared <video> into a page's player container. */
  mountVideo: (container: HTMLElement | null) => void;

  /** Fade the full-screen branded transition overlay in (and play it). */
  playTransition: () => void;

  /** Prefetch sources for an experience and load segment 0 (muted). */
  prepare: (videos: VideoT[]) => Promise<void>;

  /** Lifecycle of the last prepare() call (see PrepareStatusT). */
  prepareStatus: PrepareStatusT;

  /** True once every segment has a playable source and segment 0 is loaded. */
  ready: boolean;

  /** Re-mint one segment's source (on expiry/error); returns the new src. */
  refresh: (url: string) => Promise<null | string>;

  /** Move the shared <video> back to its off-screen home. */
  releaseVideo: () => void;

  /** The currently-known source URL for a segment (by its Vimeo url). */
  sourceFor: (url: string) => null | string;

  /** True while the branded transition overlay is shown (intro/segment/outro). */
  transitioning: boolean;

  /** Synchronous unmute + play, for the Start gesture (call inside onClick). */
  unlock: () => void;

  /** Segment urls from the last prepare() that have no resolvable source. */
  unplayable: string[];
};

const PlaybackContext = createContext<PlaybackContextT | null>(null);

export function usePlayback(): PlaybackContextT {
  const ctx = useContext(PlaybackContext);
  if (!ctx) throw Error('usePlayback() requires <PlaybackProvider>');
  return ctx;
}

export const PlaybackProvider: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  const homeRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioModuleRef = useRef<
    Awaited<ReturnType<typeof getAudioClientService>> | null
  >(null);
  const sourcesRef = useRef<Record<string, SegmentSourceT>>({});
  const [prepareStatus, setPrepareStatus] = useState<PrepareStatusT>('idle');
  const [unplayable, setUnplayable] = useState<string[]>([]);
  const ready = prepareStatus === 'ready';

  // The full-screen branded logo-fade overlay. It lives here (above the routes)
  // so it can fade in on the "Start Your Session" tap and persist across the SPA
  // navigation into the Session page before fading out to segment 0. It MUST stay
  // muted — this is a SEPARATE <video> that never gets the gesture unlock, so it
  // relies on muted-autoplay. Hidden by default (fades IN when triggered).
  const [transitioning, setTransitioning] = useState(false);
  const transitionRef = useRef<HTMLVideoElement | null>(null);
  const transitionShownAtRef = useRef(0);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionMaxRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fired once when the fade-out actually begins - lets a caller start the held
  // clip exactly as the logo starts to clear, instead of under it.
  const onFadeOutRef = useRef<(() => void) | null>(null);

  // Create the persistent <video> ONCE, imperatively (client-only). Building it
  // via the DOM rather than JSX keeps React out of its lifecycle, so re-parenting
  // it between pages never triggers a re-create (which would lose the unlock).
  useEffect(() => {
    const home = homeRef.current;
    if (!home || videoRef.current) return undefined;

    const v = document.createElement('video');
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.preload = 'auto';
    v.style.width = '100%';
    v.style.height = '100%';
    v.style.objectFit = 'contain';
    v.style.background = 'black';
    home.appendChild(v);
    videoRef.current = v;

    return () => {
      v.pause();
      v.removeAttribute('src');
      v.load();
      v.remove();
      videoRef.current = null;
    };
  }, []);

  // Unlock the shared AudioContext for iOS on any user gesture. The build screen
  // reaches its narration with no gesture of its own, so an earlier tap must
  // unlock it. Kept listening (not one-shot) to re-prime after iOS re-suspends.
  useEffect(() => {
    // Preload so unlockAudio() can run synchronously within the gesture.
    void getAudioClientService()
      .then((m) => {
        audioModuleRef.current = m;
      })
      .catch(() => undefined);

    const onGesture = (): void => {
      audioModuleRef.current?.unlockAudio();
    };
    document.addEventListener('click', onGesture);
    document.addEventListener('touchend', onGesture);

    return () => {
      document.removeEventListener('click', onGesture);
      document.removeEventListener('touchend', onGesture);
    };
  }, []);

  const getVideo = useCallback(() => videoRef.current, []);

  const sourceFor = useCallback(
    (url: string): null | string => sourcesRef.current[url]?.src ?? null,
    [],
  );

  const mountVideo = useCallback((container: HTMLElement | null) => {
    const v = videoRef.current;
    if (v && container && v.parentElement !== container) {
      container.appendChild(v);
    }
  }, []);

  const releaseVideo = useCallback(() => {
    const v = videoRef.current;
    const home = homeRef.current;
    if (v && home && v.parentElement !== home) home.appendChild(v);
  }, []);

  const prepare = useCallback(async (videos: VideoT[]) => {
    setPrepareStatus('preparing');
    setUnplayable([]);
    const now = Date.now();
    const entries = await Promise.all(videos.map(async (vid) => {
      const links = await fetchVimeoLinks(vid.url).catch(() => null);
      const src = links ? pickSource(links) : null;
      return [vid.url, { mintedAt: now, src }] as const;
    }));
    sourcesRef.current = Object.fromEntries(entries);

    // Load segment 0 (muted) so the Start gesture can unlock it with no async gap.
    const first = videos[0] ? sourcesRef.current[videos[0].url]?.src : null;
    const v = videoRef.current;
    if (v && first) {
      v.muted = true;
      v.src = first;
      v.load();
    }

    // Surface any segments with no source so the builder can explain itself
    // instead of silently disabling Start.
    setUnplayable(entries.filter(([, s]) => !s.src).map(([url]) => url));

    // Gate Start on segment 0 having a source (the unlock target). Later
    // segments lazy-refresh via the Session error handler if missing.
    setPrepareStatus(first ? 'ready' : 'blocked');
  }, []);

  const refresh = useCallback(async (url: string): Promise<null | string> => {
    const links = await fetchVimeoLinks(url).catch(() => null);
    const src = links ? pickSource(links) : null;
    sourcesRef.current = {
      ...sourcesRef.current,
      [url]: { mintedAt: Date.now(), src },
    };
    return src;
  }, []);

  const unlock = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    // Prime within the Start gesture to unlock iOS audio, then immediately HOLD
    // segment 0 at the start so it doesn't play under the intro logo - the
    // Session resumes it (programmatically) once the fade-out begins.
    void v.play().then(() => {
      v.pause();
      try {
        v.currentTime = 0;
      } catch { /* metadata not ready yet — ignore */ }
    }).catch(() => undefined);
  }, []);

  // Begin the fade-out now: clear both timers, fire the one-shot onFadeOut
  // callback (so a held clip starts exactly as the logo clears), and hide.
  const hideTransition = useCallback(() => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (transitionMaxRef.current) {
      clearTimeout(transitionMaxRef.current);
      transitionMaxRef.current = null;
    }
    setTransitioning(false);
    const onFadeOut = onFadeOutRef.current;
    onFadeOutRef.current = null;
    onFadeOut?.();
  }, []);

  const playTransition = useCallback(() => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (transitionMaxRef.current) {
      clearTimeout(transitionMaxRef.current);
    }
    // New transition supersedes any stale held-clip resume.
    onFadeOutRef.current = null;
    transitionShownAtRef.current = Date.now();
    setTransitioning(true);
    const t = transitionRef.current;
    if (t) {
      try {
        t.currentTime = 0;
      } catch { /* metadata not ready yet — ignore */ }
      void t.play().catch(() => undefined);
    }
    // Safety cap: never let a stalled or failed load strand the overlay.
    transitionMaxRef.current = setTimeout(hideTransition, TRANSITION_MAX_MS);
  }, [hideTransition]);

  // Fade the logo out, but hold it a minimum window first so a fast swap (or a
  // fast navigation into the Session) still shows a full, deliberate transition.
  // `onFadeOut` (optional) fires once the fade-out actually begins, so a held
  // clip is revealed from its start instead of playing under the logo.
  const endTransition = useCallback((onFadeOut?: () => void) => {
    if (onFadeOut) onFadeOutRef.current = onFadeOut;
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    const elapsed = Date.now() - transitionShownAtRef.current;
    if (elapsed >= TRANSITION_MIN_MS) hideTransition();
    else {
      transitionTimerRef.current = setTimeout(
        hideTransition,
        TRANSITION_MIN_MS - elapsed,
      );
    }
  }, [hideTransition]);

  // Clear any pending timers on unmount.
  useEffect(() => () => {
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    if (transitionMaxRef.current) clearTimeout(transitionMaxRef.current);
  }, []);

  const value = useMemo<PlaybackContextT>(() => ({
    endTransition,
    getVideo,
    mountVideo,
    playTransition,
    prepare,
    prepareStatus,
    ready,
    refresh,
    releaseVideo,
    sourceFor,
    transitioning,
    unlock,
    unplayable,
  }), [
    endTransition,
    getVideo,
    mountVideo,
    playTransition,
    prepare,
    prepareStatus,
    ready,
    refresh,
    releaseVideo,
    sourceFor,
    transitioning,
    unlock,
    unplayable,
  ]);

  return (
    <PlaybackContext.Provider value={value}>
      {children}
      {/* Off-screen home for the shared <video> when no page hosts it. */}
      <div
        aria-hidden
        ref={homeRef}
        style={{
          height: 1,
          left: -9999,
          opacity: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          position: 'fixed',
          top: -9999,
          width: 1,
        }}
      />

      {/* Full-screen branded logo-fade. Black bg masks the page swap during
          navigation; muted so it can autoplay; pointer-events: none so it
          never blocks the UI. Opacity drives the fade in/out. */}
      <video
        loop
        muted
        playsInline
        preload="auto"
        ref={transitionRef}
        src={transitionVideo}
        style={{
          background: 'black',
          height: '100%',
          left: 0,
          objectFit: 'cover',
          opacity: transitioning ? 1 : 0,
          pointerEvents: 'none',
          position: 'fixed',
          top: 0,
          transition: 'opacity 0.4s ease',
          width: '100%',
          zIndex: 2000,
        }}
      />
    </PlaybackContext.Provider>
  );
};
